/* eslint-disable max-lines-per-function */

import stylesheet from './recommendations.component.scss?as=stylesheet';
import {html, nothing, LitElement} from 'lit';
import GTM from 'extensions/gtm';
import Recommendations from '../../../Recommendations';
import 'custom-extensions/ui-components/x-product-card';
import 'custom-extensions/ui-components/x-splide';

/**
 * Constructor product recommendations carousel.
 *
 * Attributes:
 * - `pod-id` (required) — Constructor pod ID to fetch recommendations for (e.g. `you-may-also-like`).
 * - `page_type` (`home` | `pdp` | `plp`; default `home`) — how the pod is seeded: `pdp` by product id,
 *   `plp` by category, `home` by the visitor's Constructor client id (no seed).
 * - `product-id` (required when `page_type="pdp"`) — Miva product ID for the active PDP; this is the
 *   value Constructor indexes as `item_id`.
 * - `product-ids` (required when `page_type="cart"`) — comma separated Miva product IDs for the
 *   basket contents, used as the pod seeds. Constructor drops seed items from its own results, so a
 *   cart pod will not recommend something already in the basket.
 * - `category-id` (required when `page_type="plp"`) — category code for the active PLP.
 * - `recommendation-type` — optional `PDP_ALSO_LIKE` or `PDP_FANS_ALSO_LIKE`, selects the DAM image
 *   transformation set applied by the `constructor-recommendations` JSON API function. Leave empty to
 *   use the global product card transformation.
 * - `title` — optional heading rendered above the carousel.
 * - `list` — GTM impression list name (default "Product Recommendations"); also used as the carousel DOM id slug.
 * - `limit` — max products to render (default 20).
 * - `carousel-options` — JSON object forwarded to the underlying Splide carousel.
 */
/* eslint-disable camelcase */
export default class SearchRecommendations extends LitElement {

	static get properties () {
		return {
			_isLoading: {
				type: Boolean
			},
			_recommendations: {
				type: Array
			},
			categoryId: {
				attribute: 'category-id',
				type: String
			},
			limit: {
				type: Number
			},
			list: {
				type: String
			},
			page_type: {
				type: String
			},
			personalized: {
				type: Boolean
			},
			podId: {
				attribute: 'pod-id',
				type: String
			},
			productId: {
				attribute: 'product-id',
				type: String
			},
			productIds: {
				attribute: 'product-ids',
				type: String
			},
			quickAdd: {
				attribute: 'quick-add',
				type: Boolean
			},
			recommendationType: {
				attribute: 'recommendation-type',
				type: String
			},
			sectionTitle: {
				attribute: 'section-title',
				type: String
			},
			slideOptions: {
				attribute: 'carousel-options',
				type: Object
			},
			title: {
				type: String
			}
		};
	};

	static get styles () {
		return [stylesheet];
	}

	get listSlug () {
		return this.slugify(this.list);
	}

	get requestKey () {
		return [
			this.podId,
			this.page_type,
			this.productId,
			this.productIds,
			this.categoryId,
			this.limit
		].join('|');
	}

	constructor () {
		super();
		this._afterRecommendationsListener = () => this.sendImpressions();
		this._onResultClick = event => this.handleResultClick(event);

		// Create gtm helper
		this.gtm = new GTM(window.mivaJS);
		this._isLoading = true;
		this._recommendations = [];
		this._meta = null;
		this.categoryId = '';
		this.podId = '';
		this.productIds = '';
		this._requestKey = null;
		this.recommendationType = '';
		this.list = 'Product Recommendations';
		this.page_type = 'home';
		this.personalized = false;
		this.productId = '';
		this.slideOptions = [];
		this.title = '';
		this.limit = 20;
		this.quickAdd = false;
		this.sectionTitle = '';
	}

	sendImpressions () {
		if (!this?._recommendations?.length) {
			return;
		}
		const gtmList = this?.list;
		this.gtm.searchSendImpressions(this.gtm.slugify(gtmList), gtmList, this?._recommendations);
	}

	async queueRecommendations () {
		if (!this.podId) {
			// eslint-disable-next-line no-console
			console.warn('<x-recommendations>: pod-id attribute is required');
			return;
		}

		if (this.page_type === 'pdp' && !this.productId) {
			// eslint-disable-next-line no-console
			console.warn('<x-recommendations>: product-id attribute is required when page_type="pdp"');
			return;
		}

		if (this.page_type === 'cart' && !this.productIds) {
			// eslint-disable-next-line no-console
			console.warn('<x-recommendations>: product-ids attribute is required when page_type="cart"');
			return;
		}

		if (this.page_type === 'plp' && !this.categoryId) {
			// eslint-disable-next-line no-console
			console.warn('<x-recommendations>: category-id attribute is required when page_type="plp"');
			return;
		}

		this._requestKey = this.requestKey;

		const {meta, products} = await Recommendations.get({
			categoryId: this.categoryId,
			limit: this.limit,
			list: this.listSlug,
			page: this.page_type,
			podId: this.podId,
			productId: this.productId,
			productIds: this.productIds,
			recommendationType: this.recommendationType
		});

		this._meta = meta;
		this._recommendations = products;
	}

	async firstUpdated () {
		await this.refresh();
	}

	/**
	 * @param {Map} changedProperties
	 */
	updated (changedProperties) {
		const seedProperties = [
			'categoryId',
			'limit',
			'page_type',
			'podId',
			'productId',
			'productIds'
		];

		if (!seedProperties.some(property => changedProperties.has(property))) {
			return;
		}

		if (this._requestKey === this.requestKey) {
			return;
		}

		this.refresh();
	}

	async refresh () {
		this._isLoading = true;

		try {
			await this.queueRecommendations();
		} finally {
			this._isLoading = false;
		}

		this.sendImpressions();

		Recommendations.trackView(this._meta, this._recommendations);
	}

	/**
	 * @param {Event} event
	 */
	handleResultClick (event) {
		const path = event.composedPath();

		if (path.some(node => ['BUTTON', 'FORM', 'INPUT', 'SELECT'].includes(node?.tagName))) {
			return;
		}

		const card = path.find(node => node?.dataset?.cnstrcItemId);

		if (!card) {
			return;
		}

		const index = this._recommendations.findIndex(product => String(product?.code) === card.dataset.cnstrcItemId);

		if (index === -1) {
			return;
		}

		Recommendations.trackClick(this._meta, this._recommendations[index], index + 1);
	}

	renderProductResults () {
		const gtmData = {
			listId: this.gtm.slugify(this?.list),
			listName: this?.list
		};

		return this._recommendations?.map((product, index) => {
			const pos = index + 1;
			const shippingFlags = {
				isBopisOnly: product?.is_bopis_only,
				isDeliveryOnly: product?.is_delivery_only,
				isInStoreOnly: product?.is_in_store_only,
				isNotForSale: product?.is_not_for_sale
			};

			return html`
				<x-product-card
					brand="${product?.brand}"
					code="${product.code}"
					hover-src-desktop="${product?.images?.secondary?.desktop}"
					hover-src-mobile="${product?.images?.secondary?.mobile}"
					gtm-list="${gtmData?.listName}"
					gtm-list-id="${gtmData?.listId}"
					gtm-position="${pos}"
					image-src-desktop="${product?.images?.main?.desktop}"
					image-src-mobile="${product?.images?.main?.mobile}"
					name="${product.name}"
					original-price="${product?.customfields?.original_price}"
					price="${product.price}"
					product-id="${product?.id}"
					shipping-flags="${shippingFlags}"
					style-number="${product?.sku}"
					url="${product.url}"
					?quick-add="${this.quickAdd}"
					section-title="${this.sectionTitle || this.title}"
					data-cnstrc-item="recommendation"
					data-cnstrc-item-id="${product?.code}"
					data-cnstrc-item-name="${product.name}"
					data-cnstrc-item-price="${product.price}"
				></x-product-card>
			`;
		});
	}

	/**
	 * Mirrors Splide's own breakpoint resolution: `slideOptions.perPage` is
	 * the desktop/default value, and `slideOptions.breakpoints` keys are
	 * max-width overrides (e.g. `{959: {perPage: 2}}` applies at
	 * <=959px). Without this, the skeleton always used the desktop count,
	 * even on mobile.
	 */
	getResponsivePerPage () {
		const breakpoints = this.slideOptions?.breakpoints || {};

		const matchedBreakpoint = Object.keys(breakpoints)
			.map(Number)
			.filter(breakpoint => window.innerWidth <= breakpoint)
			.sort((a, b) => a - b)[0];

		const perPage = matchedBreakpoint === void 0
			? this.slideOptions?.perPage
			: breakpoints[matchedBreakpoint]?.perPage;

		return Number(perPage);
	}

	renderSkeleton () {
		const cardCount = Math.max(1, this.getResponsivePerPage() || 5);

		return html`
		<div class="x-related-products x-related-products--loading" aria-hidden="true">
			<div class="x-layout__item u-width-12">
				${this.title ? html`<h2 class="recommendations__title">${this.title}</h2>` : nothing}
				<div class="recommendations__skeleton">
					${Array.from({length: cardCount}).map(() => html`
						<div class="recommendations__skeleton-card">
							<div class="recommendations__skeleton-image"></div>
							<div class="recommendations__skeleton-line recommendations__skeleton-line--name"></div>
							<div class="recommendations__skeleton-line recommendations__skeleton-line--price"></div>
						</div>
					`)}
				</div>
			</div>
		</div>
		`;
	}

	render () {
		if (this._isLoading) {
			return this.renderSkeleton();
		}

		if (!this._recommendations?.length) {
			return nothing;
		}

		return html`
		<div id="js-${this.listSlug}-carousel" class="x-related-products" @click="${this._onResultClick}" data-cnstrc-recommendations data-cnstrc-recommendations-pod-id="${this.podId}" data-cnstrc-num-results="${this._recommendations.length}" data-cnstrc-recommendations-seed-items="${this.productIds || this.productId}">
			<div class="x-layout__item u-width-12">
				${this.title ? html`<h2 class="recommendations__title">${this.title}</h2>` : nothing}
				<x-splide .type="${this.type}" .options="${this.slideOptions}">
					${this.renderProductResults()}
				</x-splide>
			</div>
		</div>
		`;
	}

	// Helpers
	htmlEntities (str) {
		// eslint-disable-next-line newline-per-chained-call
		return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	/**
	 * Slugify a string
	 * @param {String} text
	 * @returns {void}
	 */
	slugify (text) {
		return text.toLowerCase().replace(/[^\w ,-]+/g, '').replace(/ +/g, '-');
	};
};

window.customElements.define('x-recommendations', SearchRecommendations);
